import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { EditOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Tabs, Tag, Spin, Button, message } from 'antd';
import { PageHeader } from '@/components/page-header';
import { ROUTES } from '@/constants';
import { STATUS_COLORS } from '../_shared/constants';
import {
  useProject,
  useCustomersForCompany,
  useUpdateProject,
  useAddTask,
  useUpdateTask,
  useDeleteTask,
  useAddMember,
  useUpdateMember,
  useRemoveMember,
} from '../_shared/hooks';
import { formatDuration } from '../_shared/utils';
import MembersList from './_components/MembersList';
import ProjectBudgetBar from './_components/ProjectBudgetBar';
import ProjectForm from './_components/ProjectForm';
import TaskList from './_components/TaskList';

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['forms', 'tables', 'messages']);
  const [editOpen, setEditOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'tasks';
  const setActiveTab = useCallback(
    (tab) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      });
    },
    [setSearchParams]
  );

  const { data: projectData, isLoading } = useProject(projectId);
  const project = projectData?.data || projectData;
  const canManage = projectData?.canManageProjects ?? false;
  const employees = useMemo(
    () => projectData?.availableEmployees || [],
    [projectData?.availableEmployees]
  );
  const { data: customers = [] } = useCustomersForCompany(project?.company);
  const updateProject = useUpdateProject();
  const addTask = useAddTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const addMember = useAddMember();
  const updateMember = useUpdateMember();
  const removeMember = useRemoveMember();

  const handleUpdate = useCallback(
    async (values) => {
      try {
        await updateProject.mutateAsync({ id: projectId, data: values });
        message.success(t('projectUpdated', { ns: 'forms' }));
        setEditOpen(false);
      } catch {
        message.error(t('error', { ns: 'messages' }));
      }
    },
    [updateProject, projectId, t]
  );

  const handleAddTask = useCallback(
    async (projId, values) => {
      try {
        await addTask.mutateAsync({ projectId: projId, data: values });
        message.success(t('taskAdded', { ns: 'forms' }));
      } catch {
        message.error(t('error', { ns: 'messages' }));
      }
    },
    [addTask, t]
  );

  const handleUpdateTask = useCallback(
    async (projId, taskId, values) => {
      try {
        await updateTask.mutateAsync({
          projectId: projId,
          taskId,
          data: values,
        });
        message.success(t('taskUpdated', { ns: 'forms' }));
      } catch {
        message.error(t('error', { ns: 'messages' }));
      }
    },
    [updateTask, t]
  );

  const handleDeleteTask = useCallback(
    async (projId, taskId) => {
      try {
        await deleteTask.mutateAsync({ projectId: projId, taskId });
        message.success(t('taskDeleted', { ns: 'forms' }));
      } catch {
        message.error(t('error', { ns: 'messages' }));
      }
    },
    [deleteTask, t]
  );

  const handleAddMember = useCallback(
    async (projId, values) => {
      try {
        await addMember.mutateAsync({ projectId: projId, data: values });
        message.success(t('memberAdded', { ns: 'forms' }));
      } catch {
        message.error(t('error', { ns: 'messages' }));
      }
    },
    [addMember, t]
  );

  const handleUpdateMember = useCallback(
    async (projId, employeeId, values) => {
      try {
        await updateMember.mutateAsync({
          projectId: projId,
          employeeId,
          data: values,
        });
        message.success(t('memberUpdated', { ns: 'forms' }));
      } catch {
        message.error(t('error', { ns: 'messages' }));
      }
    },
    [updateMember, t]
  );

  const handleRemoveMember = useCallback(
    async (projId, employeeId) => {
      try {
        await removeMember.mutateAsync({ projectId: projId, employeeId });
        message.success(t('memberRemoved', { ns: 'forms' }));
      } catch {
        message.error(t('error', { ns: 'messages' }));
      }
    },
    [removeMember, t]
  );

  const tabItems = useMemo(() => {
    const items = [
      {
        key: 'tasks',
        label: t('tasks', { ns: 'forms' }),
        children: (
          <TaskList
            projectId={projectId}
            members={project?.members || []}
            onAddTask={handleAddTask}
            onUpdateTask={handleUpdateTask}
            onDeleteTask={handleDeleteTask}
            mutationLoading={
              addTask.isPending || updateTask.isPending || deleteTask.isPending
            }
            canManage={canManage}
          />
        ),
      },
      {
        key: 'members',
        label: t('members', { ns: 'forms' }),
        children: (
          <MembersList
            projectId={projectId}
            employees={employees}
            onAddMember={handleAddMember}
            onUpdateMember={handleUpdateMember}
            onRemoveMember={handleRemoveMember}
            mutationLoading={
              addMember.isPending ||
              updateMember.isPending ||
              removeMember.isPending
            }
            canManage={canManage}
          />
        ),
      },
    ];

    if (canManage) {
      items.push({
        key: 'budget',
        label: t('analytics', { ns: 'forms' }),
        children: (
          <ProjectBudgetBar
            projectId={projectId}
            budgetHours={project?.budgetHours}
            trackedHours={(project.trackedMinutes || 0) / 60}
            billableHours={(project.billableMinutes || 0) / 60}
            analytics={project?.analytics || {}}
            currency={project?.currency?.toUpperCase() || ''}
            tasks={project?.tasks || []}
            members={project?.members || []}
          />
        ),
      });
    }

    return items;
  }, [
    t,
    project,
    projectId,
    employees,
    canManage,
    handleAddTask,
    handleUpdateTask,
    handleDeleteTask,
    handleAddMember,
    handleUpdateMember,
    handleRemoveMember,
    addTask.isPending,
    updateTask.isPending,
    deleteTask.isPending,
    addMember.isPending,
    updateMember.isPending,
    removeMember.isPending,
  ]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="py-8 text-center">{t('notFound', { ns: 'forms' })}</div>
    );
  }

  return (
    <>
      <PageHeader>
        <div className="flex w-full items-center gap-3">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`${ROUTES.admin}/${ROUTES.timeTracking}`)}
            className="btn-default"
          >
            {t('back', { ns: 'forms' })}
          </Button>

          <div className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: project.color }}
            />
            <span className="text-base font-semibold">{project.name}</span>
            {project.code && (
              <span className="text-sm text-gray-400">({project.code})</span>
            )}
            <Tag color={STATUS_COLORS[project.status]}>
              {t(project.status, { ns: 'forms' })}
            </Tag>
            {project.client?.name && (
              <span className="text-sm text-gray-400">
                — {project.client.name}
              </span>
            )}
          </div>

          {canManage && (
            <Button
              icon={<EditOutlined />}
              onClick={() => setEditOpen(true)}
              className="btn-default ml-auto"
            >
              {t('edit', { ns: 'forms' })}
            </Button>
          )}
        </div>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-2">
        {project.description && (
          <p className="text-sm text-gray-500">{project.description}</p>
        )}

        <div className="flex flex-wrap gap-6 text-sm text-gray-500">
          <span>
            {t('projectName', { ns: 'forms' })}: <strong>{project.name}</strong>
          </span>
          {project.client?.name && (
            <span>
              {t('client', { ns: 'forms' })}: {project.client.name}
            </span>
          )}
          {canManage && project.billable && (
            <span>
              {t('hourlyRate', { ns: 'forms' })}: {project.hourlyRate}{' '}
              {project.currency?.toUpperCase()}
            </span>
          )}
          {canManage && project.budgetHours && (
            <span>
              {t('budget', { ns: 'forms' })}:{' '}
              {formatDuration(project.budgetHours * 60)}
            </span>
          )}
        </div>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

      {canManage && (
        <ProjectForm
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSubmit={handleUpdate}
          initialValues={project}
          customers={customers}
          isLoading={updateProject.isPending}
        />
      )}
    </>
  );
}
